import React from "react";

/**
 * Shared primitives for the three submission detail bodies.
 *
 * Ported from the website studio's Valuations view, including its rule that an
 * empty value still renders (muted) rather than disappearing: "we never asked"
 * and "they left it blank" must not look identical.
 */

export function Spec({
  label,
  value,
  mono,
}: {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
}) {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="flex gap-3 py-1.5 border-b border-border/40 last:border-0 text-sm">
      <span className="w-40 shrink-0 text-muted-foreground">{label}</span>
      <span
        className={[
          "flex-1 break-words",
          mono ? "font-mono text-xs" : "",
          empty ? "text-muted-foreground/50" : "",
        ].join(" ")}
      >
        {empty ? "—" : value}
      </span>
    </div>
  );
}

export function Block({
  title,
  children,
  note,
}: {
  title: string;
  children: React.ReactNode;
  note?: string;
}) {
  return (
    <section className="rounded-lg border border-border p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
        {title}
      </h3>
      {note && <p className="text-xs text-muted-foreground/80 mb-3 leading-relaxed">{note}</p>}
      <div>{children}</div>
    </section>
  );
}

export function Chips({ values }: { values: unknown }) {
  const list = Array.isArray(values) ? values.filter(Boolean) : [];
  if (!list.length) return <span className="text-muted-foreground/50">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((v, i) => (
        <span key={i} className="rounded bg-muted px-1.5 py-0.5 text-xs">
          {String(v)}
        </span>
      ))}
    </div>
  );
}

/** Multi-line prose from the payload, preserving the model's paragraphing. */
export function Prose({ text }: { text: unknown }) {
  const s = text ? String(text) : "";
  if (!s.trim()) return <span className="text-muted-foreground/50">—</span>;
  return <p className="whitespace-pre-wrap text-sm leading-relaxed">{s}</p>;
}

export const fmtDateTime = (v: unknown) => {
  if (!v) return null;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
};

export const fmtDate = (v: unknown) => {
  if (!v) return null;
  const d = new Date(String(v));
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const str = (v: unknown) => (v === null || v === undefined || v === "" ? null : String(v));

/** Money from a decimal column that arrives as a string. */
export const money = (v: unknown) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString()}`;
};

/**
 * Buyer deal-size columns are stored IN THOUSANDS (5000 = $5M). Formatting them
 * with `money` above would render $5,000 and understate every buyer by 1000x.
 */
export const thousands = (v: unknown) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n >= 1000 ? `$${(n / 1000).toFixed(1)}M` : `$${n}K`;
};

/**
 * A link target that is safe to put in an href, or null.
 *
 * Every URL rendered by these views is attacker-controlled: `onePagerUrl`,
 * `companyWebsite` and friends are free-text fields on public website forms,
 * and the website's zod schema does not validate them as URLs. React does not
 * block `javascript:` in an href — it warns in development and renders it
 * anyway — so a buyer registering with `javascript:...` as their one-pager
 * would get script execution in the CRM's own origin the moment someone
 * clicked "View PDF".
 *
 * Only http and https survive. A bare host ("acme.com") is assumed https,
 * since that is how these fields are usually filled in. Anything else — a
 * data:, javascript:, vbscript: or file: URL, or something unparseable —
 * returns null, and the caller renders plain text instead of a link.
 */
export function safeHref(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  // Scheme detection ignores whitespace and control characters, because
  // browsers strip those when parsing: "java\tscript:alert(1)" navigates as
  // a javascript: URL, so a check against the raw string can be walked past.
  const stripped = trimmed.replace(/[\s\u0000-\u001F\u007F]/g, "");
  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(stripped);
  const candidate = hasScheme ? stripped : `https://${trimmed}`;

  try {
    const u = new URL(candidate);
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}

export const range = (lo: unknown, hi: unknown, fmt: (v: unknown) => string | null = money) => {
  const a = fmt(lo);
  const b = fmt(hi);
  if (!a && !b) return null;
  return `${a ?? "?"} – ${b ?? "?"}`;
};
